import { createContext, useContext, useSyncExternalStore } from 'react';
import type { ObjectDefinitionSpec, ObjectParamValue } from 'shared-types';
import type { ObjectDefinitionFieldsFragment } from '../../types/graphqlTypes';
import type { LibraryCategoryOption } from '../objectLibrary/categories';
import type { EditorState, EditorStore } from './document/EditorStore';

export type EditorTool = 'select' | 'node' | 'pan' | 'line' | 'polyline' | 'pen' | 'rect' | 'circle' | 'arc';

export const DRAWING_TOOLS: readonly EditorTool[] = ['line', 'polyline', 'pen', 'rect', 'circle', 'arc'];

export type EditorMode = 'edit' | 'test';
export type EditorView = '2d' | 'split' | '3d';

/**
 * A placed-object stand-in for Test mode: the size and params a user would
 * set in a project. An unset size follows the object's default size, so
 * editing the default size in Edit mode carries over to Test mode.
 */
export interface TestInstance {
  width?: number;
  depth?: number;
  height?: number;
  params: Readonly<Record<string, ObjectParamValue>>;
  /** Where the test object's center sits on the canvas (a run resized from one end moves it). */
  center?: readonly [number, number];
  /** Doors / windows: the test wall's thickness (m). */
  wall?: number;
  /** Doors / windows: the test wall's radius (m); unset / 0 = straight, negative bends the other way. */
  wallRadius?: number;
  /** Doors: hinge at the far (+u) jamb, like the placed opening's hinge flip. */
  hingeAtEnd?: boolean;
  /** Doors / windows: opening toward −n, like the placed opening's side flip. */
  otherSide?: boolean;
}

/** The test size in effect: the overrides, else the default size. */
export function testSize(test: TestInstance, spec: ObjectDefinitionSpec): { width: number; depth: number; height: number } {
  return { width: test.width ?? spec.defaultSize.width, depth: test.depth ?? spec.defaultSize.depth, height: test.height ?? spec.defaultSize.height };
}

export interface EditorUi {
  tool: EditorTool;
  /** Grid step in meters; 0 = no grid snapping. */
  grid: number;
  snapToPoints: boolean;
  mode: EditorMode;
  view: EditorView;
  test: TestInstance;
  /** A published version shown read-only (History tab); null = the draft being edited. */
  preview: { version: number; spec: ObjectDefinitionSpec } | null;
  /** The point picked with the Edit points tool (its pin rules show in the inspector). */
  node: { shapeId: string; index: number } | null;
  /** A smart object's 3D model is being placed (its pencil in the 3D view): the edit tab's model controls, in Test mode. */
  editModel: boolean;
}

export interface ObjectEditorContextValue {
  store: EditorStore;
  organisationId: string;
  definition: ObjectDefinitionFieldsFragment;
  categories: readonly LibraryCategoryOption[];
  ui: EditorUi;
  setUi: (update: (ui: EditorUi) => EditorUi) => void;
  /** Replace the draft with a spec the server already saved (restore / discard): new history, nothing to autosave. */
  resetDraft: (spec: ObjectDefinitionSpec) => void;
}

export const ObjectEditorContext = createContext<ObjectEditorContextValue | null>(null);

export function useObjectEditor(): ObjectEditorContextValue {
  const value = useContext(ObjectEditorContext);
  if (!value) throw new Error('useObjectEditor outside ObjectEditorContext');
  return value;
}

/** The live document (spec, selection, history flags). */
export function useEditorState(): EditorState {
  const { store } = useObjectEditor();
  return useSyncExternalStore(store.subscribe, store.getState);
}
